import { useState, type ReactElement } from 'react';
import { Download, FileText, Trash2, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ClinicalSection,
  type ClinicalSectionLabels,
} from '@/features/patient-record/components/ClinicalSection';
import { ConfirmDeleteDialog } from '@/features/patient-record/components/_shared/ConfirmDeleteDialog';
import { RowActions, RowActionButton } from '@/features/patient-record/components/_shared/RowActions';
import { familyQueries } from '@/features/family-record/queries';
import {
  useDeleteFamilyDocument,
  useUploadFamilyDocument,
} from '@/features/family-record/hooks/useFamilyDocumentMutations';
import type { FamilyDocumentViewModel } from '@/features/family-record/mappers/familyDocument';
import { downloadBlob } from '@/lib/downloadFile';
import { formatDate } from '@/lib/format';
import { notifyApiError } from '@/lib/notifyApiError';

import { UploadFamilyDocumentDialog } from './UploadFamilyDocumentDialog';

interface DocumentsSectionProps {
  readonly guid: string;
}

/**
 * Family documents — reworked to the patient record pattern (PTV2-1157): a
 * collapsible `ClinicalSection` with an eye/view-state summary, the "Upload"
 * affordance in the card header, and `RowActions` per row.
 */
export function DocumentsSection({ guid }: DocumentsSectionProps): ReactElement {
  const { t } = useTranslation();
  const query = useQuery(familyQueries.documents(guid));
  const uploadMutation = useUploadFamilyDocument(guid);
  const deleteMutation = useDeleteFamilyDocument(guid);
  const [uploading, setUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<FamilyDocumentViewModel | null>(null);

  const labels: ClinicalSectionLabels = {
    title: t('routes.family.documents.heading'),
    empty: t('routes.family.documents.empty'),
    emptyIcon: <FileText className="h-5 w-5" aria-hidden="true" />,
    loading: t('routes.family.documents.loading'),
    errorHeading: t('routes.family.documents.error.heading'),
    errorMessage: t('routes.family.documents.error.message'),
    codeLabel: t('routes.patient.error.codeLabel'),
    retryLabel: t('routes.family.error.retry'),
  };

  return (
    <>
      <ClinicalSection
        id="documents"
        labels={labels}
        query={query}
        summary={(query.data ?? []).map((d) => ({
          label: d.fileName ?? (d.title.length > 0 ? d.title : t('routes.family.documents.unnamed')),
          value: t(`routes.family.documents.type.${d.type}`),
          filled: true,
        }))}
        itemKey={(d) => d.id}
        renderItem={(d) => (
          <DocumentRow
            document={d}
            onDelete={() => {
              setPendingDelete(d);
            }}
          />
        )}
        headerAction={
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setUploading(true);
            }}
          >
            <Upload className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            {t('routes.family.documents.upload')}
          </Button>
        }
      />

      {uploading && (
        <UploadFamilyDocumentDialog
          busy={uploadMutation.isPending}
          onCancel={() => {
            setUploading(false);
          }}
          onSubmit={(variables) => {
            uploadMutation.mutate(variables, {
              onSuccess: () => {
                setUploading(false);
              },
            });
          }}
        />
      )}

      {pendingDelete !== null && (
        <ConfirmDeleteDialog
          title={t('routes.family.documents.confirmDelete.title')}
          message={t('routes.family.documents.confirmDelete.message')}
          confirmLabel={t('routes.family.documents.confirmDelete.confirm')}
          busy={deleteMutation.isPending}
          onCancel={() => {
            setPendingDelete(null);
          }}
          onConfirm={() => {
            deleteMutation.mutate(pendingDelete.id, {
              onSettled: () => {
                setPendingDelete(null);
              },
            });
          }}
        />
      )}
    </>
  );
}

interface DocumentRowProps {
  readonly document: FamilyDocumentViewModel;
  readonly onDelete: () => void;
}

function DocumentRow({ document: doc, onDelete }: DocumentRowProps): ReactElement {
  const { t } = useTranslation();
  const label =
    doc.fileName ??
    (doc.title.length > 0 ? doc.title : t('routes.family.documents.unnamed'));
  const handleDownload = (): void => {
    downloadBlob(`/attachments/${encodeURIComponent(doc.id)}`, doc.fileName ?? doc.title).catch(
      notifyApiError,
    );
  };
  return (
    <div className="group flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <FileText className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{label}</p>
          <p className="text-xs text-muted-foreground">
            <Badge variant="outline" className="text-[10px] h-4 mr-2 uppercase tracking-wide">
              {t(`routes.family.documents.type.${doc.type}`)}
            </Badge>
            {doc.uploadedAt !== null && formatDate(doc.uploadedAt)}
            {doc.uploadedBy !== null && (
              <>
                {' · '}
                {doc.uploadedBy}
              </>
            )}
          </p>
        </div>
      </div>
      <RowActions>
        <RowActionButton
          icon={<Download className="h-3.5 w-3.5" aria-hidden="true" />}
          label={t('routes.family.documents.download')}
          onClick={handleDownload}
        />
        <RowActionButton
          icon={<Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />}
          label={t('routes.family.documents.removeAria', { name: label })}
          onClick={onDelete}
        />
      </RowActions>
    </div>
  );
}
